<template>
  <div class="team-builder">
    <!-- People table ----------------------------------------------------- -->
    <div
      class="people-table"
      :class="{ 'people-table--dense': isDense }"
    >
      <div class="people-table__head">
        <div>Name</div>
        <div>Email</div>
        <div>Team</div>
        <div>Manager</div>
        <div></div>
      </div>

      <div
        v-for="(person, idx) in internalPeople"
        :key="person._rid"
        class="people-row"
        :class="{ you: !!person.you }"
      >
        <!-- Name cell ------------------------------------------------ -->
        <div class="cell">
          <span class="cell-label" aria-hidden="true">Name</span>
          <div class="name-cell">
            <span
              class="person-dot"
              :class="avatarClass(person)"
              :title="initialsFor(person)"
              aria-hidden="true"
            ></span>

            <input
              ref="nameInputs"
              class="input"
              type="text"
              :value="person.name"
              :placeholder="person.you ? 'Your full name' : 'Full name'"
              :data-rid="person._rid"
              @input="(e) => updateField(idx, 'name', e.target.value)"
            />
            <span v-if="person.you" class="you-badge">You</span>
          </div>
        </div>

        <!-- Email cell ----------------------------------------------- -->
        <div class="cell">
          <span class="cell-label" aria-hidden="true">Email</span>
          <span v-if="person.you" class="static email">{{ person.email }}</span>
          <template v-else>
            <input
              class="input cell-plain"
              type="email"
              :value="person.email"
              placeholder="email@company.com"
              :class="{ 'has-error': !!emailError(idx) }"
              @input="(e) => updateField(idx, 'email', e.target.value)"
              @focus="onEmailFocus(idx)"
              @blur="onEmailBlur(idx)"
            />
            <span v-if="emailError(idx)" class="cell-error">{{ emailError(idx) }}</span>
          </template>
        </div>

        <!-- Team cell ------------------------------------------------ -->
        <div class="cell">
          <span class="cell-label" aria-hidden="true">Team</span>
          <select
            :key="`team-select-${idx}-${teamSelectGen}`"
            class="input cell-plain"
            :value="person.team || ''"
            @change="(e) => onTeamChange(idx, e.target.value)"
          >
            <option value="" disabled>Select a team…</option>
            <option
              v-for="t in teamOptions"
              :key="t"
              :value="t"
            >{{ t }}</option>
            <option value="__NEW__">+ New team…</option>
          </select>
        </div>

        <!-- Manager cell --------------------------------------------- -->
        <div class="cell">
          <span class="cell-label" aria-hidden="true">Reports to</span>
          <div class="manager-cell-wrap" :data-idx="idx">
            <button
              type="button"
              class="manager-cell"
              :class="{ 'manager-cell--top': isTopOfOrg(person) }"
              @click.stop="toggleManagerDropdown(idx)"
            >
              <template v-if="isTopOfOrg(person)">
                <span class="manager-cell__top-marker" aria-hidden="true">★</span>
                <span class="manager-cell__name">Top of org</span>
              </template>
              <template v-else-if="managerForRow(idx)">
                <span
                  class="person-dot"
                  :class="avatarClass(managerForRow(idx))"
                  aria-hidden="true"
                ></span>
                <span class="manager-cell__name">{{ managerNameLabel(managerForRow(idx)) }}</span>
              </template>
              <span v-else class="manager-cell__name manager-cell__name--placeholder">
                Pick a manager
              </span>
              <span class="manager-cell__caret">▾</span>
            </button>
            <ul
              v-if="openManagerIdx === idx"
              class="manager-dropdown"
              role="listbox"
            >
              <li
                v-if="!isTopOfOrg(person)"
                class="manager-dropdown__item manager-dropdown__item--top"
                @click="setManager(idx, null)"
              >
                <span class="manager-cell__top-marker" aria-hidden="true">★</span>
                <span class="manager-dropdown__name">Top of org</span>
                <span class="manager-dropdown__hint">No manager</span>
              </li>
              <li
                v-for="candidate in managerCandidates(idx)"
                :key="`mgr-${candidate._rid}`"
                class="manager-dropdown__item"
                role="option"
                @click="setManager(idx, candidate.email)"
              >
                <span
                  class="avatar sm"
                  :class="avatarClass(candidate)"
                >{{ initialsFor(candidate) }}</span>
                <span class="manager-dropdown__name">{{ managerNameLabel(candidate) }}</span>
              </li>
              <li
                v-if="isTopOfOrg(person) && !managerCandidates(idx).length"
                class="manager-dropdown__empty"
              >Add another person to assign them as your manager.</li>
            </ul>
          </div>
        </div>

        <!-- Remove cell --------------------------------------------- -->
        <div class="cell">
          <button
            v-if="!person.you"
            type="button"
            class="people-row__remove"
            :aria-label="`Remove ${person.name || person.email || 'person'}`"
            @click="removePerson(idx)"
          >×</button>
        </div>
      </div>
    </div>

    <!-- Add another person --------------------------------------------- -->
    <button
      type="button"
      class="add-person"
      @click="addPerson"
    >+ Add another person</button>

    <!-- New-team modal — opens when the user picks "+ New team…" in any
         row's team select. Replaces the cramped inline editor. -->
    <NewTeamModal
      :visible="newTeamModalOpen"
      :existing-teams="teamOptions"
      @close="closeNewTeamModal"
      @submit="commitNewTeamFromModal"
    />

    <!-- Live counter --------------------------------------------------- -->
    <div class="people-meta">
      <span :class="{ 'people-meta--over': overCap }">
        <strong>{{ internalPeople.length }}</strong>
        of {{ memberCap }} invited
        <span class="people-meta__sep">·</span>
        {{ teamCount }} {{ teamCount === 1 ? 'team' : 'teams' }}
      </span>
      <span class="people-meta__hint">You can reassign managers later in Settings.</span>
    </div>
  </div>
</template>

<script>
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Five color buckets for team identity, matching the mockup CSS
// (`.c1` … `.c5`). Plus `.c0` — a neutral grey used when the row has
// no team picked yet so the dot doesn't lie about membership.
const AVATAR_BUCKETS = ['c1', 'c2', 'c3', 'c4', 'c5']

// Stable per-row IDs prevent the v-for key from changing as the user
// types in name/email fields (the previous key included `person.email`,
// which destroyed the input's DOM on every keystroke and dropped focus).
let _ridCounter = 0
function nextRid() {
  _ridCounter += 1
  return `r${_ridCounter}-${Date.now().toString(36)}`
}

/**
 * Pure hash mapping a team name to one of the five color buckets so
 * everyone on the same team shares a color. Empty / missing team
 * returns `c0` (neutral grey). Stable across reloads — the hash is
 * deterministic on the team string.
 */
function colorForTeam(team) {
  const s = String(team || '').trim().toLowerCase()
  if (!s) return 'c0'
  let h = 0
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0
  }
  return AVATAR_BUCKETS[h % AVATAR_BUCKETS.length]
}

/**
 * TeamBuilder — flat people-table editor for the signup wizard Step 4.
 * Owned by Phase D Track D2. The signer is pinned at index 0 (seeded by
 * the parent via the signup module's verifyPin action) and rendered
 * read-only with a "You" badge; everyone else is fully editable.
 *
 * Props:
 *  - value: Array<Person>
 *     Person = {
 *       name: string,
 *       email: string,
 *       team: string,
 *       managerEmail: string | null,
 *       you?: boolean,
 *     }
 *  - memberCap:    Number — total invites allowed (default 20, free-tier cap).
 *  - signerEmail:  String — fallback used to (re)seed the signer row if
 *                            the parent forgot to.
 *
 * Emits:
 *  - input (nextPeople)  — called on any edit; parent commits via
 *                          `signup/SET_PEOPLE`.
 *  - validity (boolean)  — true when all rows are valid AND total <= cap.
 */
import NewTeamModal from './NewTeamModal.vue'

export default {
  name: 'TeamBuilder',
  components: { NewTeamModal },
  props: {
    value:       { type: Array, default: () => [] },
    memberCap:   { type: Number, default: 20 },
    signerEmail: { type: String, default: '' },
    /**
     * Company name from Step 1 — used as the default team for the
     * signer row when they haven't picked a team yet. Optional; if
     * empty, signer's team starts blank like everyone else.
     */
    companyName: { type: String, default: '' },
  },
  data() {
    return {
      internalPeople: this.cloneIncoming(this.value),
      // New-team flow now uses a focused modal instead of a cramped
      // inline editor. We track which row triggered it so the result
      // is committed back to the correct person on submit.
      newTeamModalOpen: false,
      newTeamModalRowIdx: -1,
      // Bumped whenever we need to force the team `<select>` to re-sync
      // its DOM value with `person.team` — picking "+ New team…" leaves
      // the native select stuck on that sentinel until Vue re-renders.
      teamSelectGen: 0,
      openManagerIdx: -1,
      // Per-row touched flag for inline email validation. We only show
      // the email error after the user has tabbed off the field at least
      // once, so an empty new row doesn't immediately scream at them.
      emailTouched: {},
    }
  },
  computed: {
    teamOptions() {
      // Distinct, in-order list of team names already used by any row.
      // Empty strings are skipped — users pick from this list to avoid
      // typo-induced duplicates ("Eng" vs "Engineering").
      const seen = new Set()
      const out = []
      this.internalPeople.forEach((p) => {
        const t = (p && p.team) || ''
        if (!t || seen.has(t)) return
        seen.add(t)
        out.push(t)
      })
      return out
    },
    teamCount() {
      return this.teamOptions.length
    },
    overCap() {
      return this.internalPeople.length > this.memberCap
    },
    /**
     * Density flips on at 10+ rows. The first few rows breathe nicely
     * with the comfortable padding; once the table starts to dominate
     * the viewport (~10 rows × 48px = ~480px), tighter rows let the
     * user keep more context visible while still being touch-friendly.
     */
    isDense() {
      return this.internalPeople.length >= 10
    },
    allRowsValid() {
      for (let i = 0; i < this.internalPeople.length; i += 1) {
        if (!this.isRowValid(i)) return false
      }
      return true
    },
    /** Exactly one row must be top-of-org (no manager assigned). */
    topOfOrgCount() {
      return this.internalPeople.filter((p) => p && !p.managerEmail).length
    },
    isValid() {
      return this.allRowsValid && !this.overCap && this.topOfOrgCount === 1
    },
  },
  watch: {
    value: {
      handler(next) {
        if (JSON.stringify(next) !== JSON.stringify(this.internalPeople)) {
          this.internalPeople = this.cloneIncoming(next)
        }
      },
      deep: true,
    },
    isValid: {
      immediate: true,
      handler(next) {
        this.$emit('validity', !!next)
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick)
    // Default the signer's team to the company name if it isn't set
    // yet, then emit so the parent's Vuex state mirrors what we render
    // (including the freshly-generated _rids that prevent input focus
    // loss as the user types).
    const signerIdx = this.internalPeople.findIndex((p) => p && p.you)
    if (
      signerIdx !== -1 &&
      !this.internalPeople[signerIdx].team &&
      this.companyName
    ) {
      this.$set(this.internalPeople, signerIdx, {
        ...this.internalPeople[signerIdx],
        team: this.companyName,
      })
    }
    this.emit()
    // Auto-focus the signer's name input on first visit (i.e. when it's
    // still empty). Skipping when the field already has a value means
    // navigating back to this step from later steps won't steal focus
    // from anyone reviewing the table.
    if (signerIdx !== -1 && !String(this.internalPeople[signerIdx].name || '').trim()) {
      const signerRid = this.internalPeople[signerIdx]._rid
      this.$nextTick(() => {
        const inputs = this.$refs.nameInputs
        if (!Array.isArray(inputs)) return
        const target = inputs.find((el) => el && el.dataset && el.dataset.rid === signerRid)
        if (target && typeof target.focus === 'function') target.focus()
      })
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    cloneIncoming(arr) {
      const list = Array.isArray(arr) ? arr.map((p) => ({
        _rid: p && p._rid ? p._rid : nextRid(),
        name: p.name || '',
        email: p.email || '',
        team: p.team || '',
        managerEmail: p.managerEmail || null,
        you: !!p.you,
      })) : []
      // Defensive — if the parent never seeded the signer, do it here so
      // the table is never empty. Idempotent: we only add a row when no
      // `you: true` row exists.
      const hasSigner = list.some((p) => p.you)
      if (!hasSigner && this.signerEmail) {
        list.unshift({
          _rid: nextRid(),
          name: '',
          email: this.signerEmail,
          team: '',
          managerEmail: null,
          you: true,
        })
      }
      return list
    },
    emit() {
      this.$emit(
        'input',
        this.internalPeople.map((p) => ({ ...p }))
      )
      this.$emit('validity', this.isValid)
    },
    initialsFor(person) {
      if (!person) return ''
      const source = String(person.name || person.email || '').trim()
      if (!source) return '?'
      // Prefer the first two name parts; otherwise fall back to the
      // first two characters of the email local-part. Always uppercase.
      if (person.name) {
        const parts = source.split(/\s+/).filter(Boolean)
        if (parts.length >= 2) {
          return (parts[0][0] + parts[1][0]).toUpperCase()
        }
        return parts[0].slice(0, 2).toUpperCase()
      }
      const local = source.split('@')[0] || source
      return local.slice(0, 2).toUpperCase()
    },
    avatarClass(person) {
      if (!person) return 'c0'
      // Dot color = team color so people on the same team share an
      // identifier. Signer status is conveyed by the "You" badge in
      // the name cell, not the dot.
      return colorForTeam(person.team)
    },
    managerNameLabel(person) {
      if (!person) return ''
      return person.name || person.email || ''
    },
    managerForRow(idx) {
      const row = this.internalPeople[idx]
      if (!row || !row.managerEmail) return null
      return this.internalPeople.find(
        (p) => p && p.email && p.email.toLowerCase() === row.managerEmail.toLowerCase()
      ) || null
    },
    isTopOfOrg(person) {
      return !!person && !person.managerEmail
    },
    findCurrentTopIdx() {
      return this.internalPeople.findIndex((p) => p && !p.managerEmail)
    },
    /**
     * True if `candidate` reports (directly or transitively) to `self`.
     * Used to keep cycle-creating options out of the manager dropdown.
     */
    isDescendant(candidate, self) {
      if (!candidate || !self || !self.email) return false
      const selfEmail = self.email.toLowerCase()
      const seen = new Set()
      let cursor = candidate
      while (cursor && cursor.managerEmail) {
        const me = (cursor.email || '').toLowerCase()
        if (me) {
          if (seen.has(me)) return false
          seen.add(me)
        }
        if (cursor.managerEmail.toLowerCase() === selfEmail) return true
        const next = this.internalPeople.find(
          (p) => p && p.email && p.email.toLowerCase() === cursor.managerEmail.toLowerCase()
        )
        if (!next) return false
        cursor = next
      }
      return false
    },
    managerCandidates(idx) {
      // Anyone in the table can be a manager EXCEPT self and self's
      // descendants. Candidates must have a non-empty email so we have
      // something to store. This frees the user from having to add their
      // boss before themselves.
      const self = this.internalPeople[idx]
      if (!self) return []
      return this.internalPeople.filter((p, i) => {
        if (i === idx) return false
        if (!p || !p.email) return false
        if (this.isDescendant(p, self)) return false
        return true
      })
    },
    isRowValid(idx) {
      const p = this.internalPeople[idx]
      if (!p) return false
      const name = String(p.name || '').trim()
      const email = String(p.email || '').trim()
      const team = String(p.team || '').trim()
      // Name is required for everyone (including the signer — they need
      // to type their actual name, not just rely on the "You" badge).
      if (!name) return false
      // Email format is required for non-signer rows. The signer's
      // email comes from the verified PIN session and is read-only, so
      // we only validate non-empty there.
      if (p.you) {
        if (!email) return false
      } else {
        if (!email || !EMAIL_RE.test(email)) return false
      }
      if (!team) return false
      // Manager must be set unless this row is top-of-org. If it IS
      // top-of-org, the global "exactly one top" check in
      // `allRowsValid` enforces uniqueness.
      if (!p.managerEmail) return true
      // Manager pointer must resolve to a real row in the table.
      const exists = this.internalPeople.some(
        (x) => x.email && x.email.toLowerCase() === String(p.managerEmail).toLowerCase()
      )
      if (!exists) return false
      return true
    },
    emailError(idx) {
      if (!this.emailTouched[idx]) return ''
      const p = this.internalPeople[idx]
      if (!p || p.you) return ''
      const email = String(p.email || '').trim()
      if (!email) return 'Email is required'
      return EMAIL_RE.test(email) ? '' : 'Enter a valid email'
    },
    onEmailFocus(idx) {
      // Mute the inline error while the user is typing the fix; the
      // next blur will re-evaluate and re-show it if still invalid.
      this.$set(this.emailTouched, idx, false)
    },
    onEmailBlur(idx) {
      this.$set(this.emailTouched, idx, true)
    },
    updateField(idx, key, value) {
      const target = this.internalPeople[idx]
      if (!target) return
      this.$set(this.internalPeople, idx, { ...target, [key]: value })
      this.emit()
    },
    onTeamChange(idx, value) {
      if (value === '__NEW__') {
        // Open the modal for this row. The native select's DOM value is
        // now "__NEW__" but `person.team` is unchanged; bump the
        // re-render key so Vue rebuilds the select node and it snaps
        // back to the row's actual team if the user cancels.
        this.newTeamModalRowIdx = idx
        this.newTeamModalOpen = true
        this.teamSelectGen += 1
        return
      }
      this.updateField(idx, 'team', value)
    },
    closeNewTeamModal() {
      this.newTeamModalOpen = false
      this.newTeamModalRowIdx = -1
    },
    commitNewTeamFromModal(name) {
      const idx = this.newTeamModalRowIdx
      const trimmed = String(name || '').trim()
      this.closeNewTeamModal()
      if (idx < 0 || !trimmed) return
      this.updateField(idx, 'team', trimmed)
      // Force a fresh rebuild of every team `<select>` so the new option
      // and the row's just-set value are patched together. Native selects
      // can mis-render when an option appears and `select.value` is set
      // in the same tick — rebuilding sidesteps the race entirely.
      this.$nextTick(() => {
        this.teamSelectGen += 1
      })
    },
    addPerson() {
      // Default the new person's manager to the current top-of-org so
      // we never end up with two managerless rows. The user can still
      // pick a different manager (or claim "Top of org") afterwards.
      const topIdx = this.findCurrentTopIdx()
      const top = topIdx !== -1 ? this.internalPeople[topIdx] : null
      const defaultManager = top && top.email ? top.email : null
      const newRid = nextRid()
      this.internalPeople.push({
        _rid: newRid,
        name: '',
        email: '',
        team: '',
        managerEmail: defaultManager,
      })
      this.emit()
      // Focus the new row's name input so the user can start typing
      // immediately. nextTick waits for the DOM to render the new row.
      this.$nextTick(() => {
        const inputs = this.$refs.nameInputs
        if (!Array.isArray(inputs)) return
        // Match by _rid rather than "last index" to be defensive
        // against re-orders or watcher-induced reclones.
        const target = inputs.find((el) => el && el.dataset && el.dataset.rid === newRid)
        if (target && typeof target.focus === 'function') {
          target.focus()
        }
      })
    },
    removePerson(idx) {
      const target = this.internalPeople[idx]
      // Block removing the signer — they're the admin who created the
      // workspace and must remain in the table.
      if (!target || target.you) return
      const removedEmail = (target.email || '').toLowerCase()
      const wasTop = !target.managerEmail
      this.internalPeople.splice(idx, 1)
      // Re-point anyone managed by the removed row at the (new) top, or
      // promote the next row to top if we just deleted the top.
      if (removedEmail) {
        this.internalPeople.forEach((p, i) => {
          if (p.managerEmail && p.managerEmail.toLowerCase() === removedEmail) {
            this.$set(this.internalPeople, i, { ...p, managerEmail: null })
          }
        })
      }
      // Ensure exactly one top-of-org survives. If the removed row was
      // top and someone else got promoted via the loop above, we may
      // now have multiple. In that case the FIRST remaining row stays
      // top and any other null managers get reassigned.
      const tops = this.internalPeople
        .map((p, i) => (p && !p.managerEmail ? i : -1))
        .filter((i) => i !== -1)
      if (tops.length === 0 && this.internalPeople.length > 0) {
        // Lost the top — promote index 0.
        this.$set(this.internalPeople, 0, {
          ...this.internalPeople[0],
          managerEmail: null,
        })
      } else if (tops.length > 1) {
        const keepIdx = tops[0]
        const keepEmail = this.internalPeople[keepIdx].email || null
        for (let i = 1; i < tops.length; i += 1) {
          const j = tops[i]
          this.$set(this.internalPeople, j, {
            ...this.internalPeople[j],
            managerEmail: keepEmail,
          })
        }
      }
      // wasTop is informational; the reconciliation above handles all
      // cases including signer-deletion (which is blocked anyway).
      void wasTop
      this.emit()
    },
    toggleManagerDropdown(idx) {
      this.openManagerIdx = this.openManagerIdx === idx ? -1 : idx
    },
    /**
     * Set the manager for a row.
     *
     * If `email` is null the user is claiming "Top of org" for this
     * row. To keep the invariant (exactly one row with no manager),
     * we find the previous top and reassign them to report to the new
     * top. We also move the new top onto the company-name team —
     * conceptually the top-most team in the hierarchy — mirroring the
     * default we apply to the signer on mount. Idempotent if the row
     * was already top.
     */
    setManager(idx, email) {
      this.openManagerIdx = -1
      const row = this.internalPeople[idx]
      if (!row) return
      if (email === null) {
        if (!row.managerEmail) return // already top — no-op
        // Find the current top and reassign them to this row.
        const newTopEmail = row.email || null
        const currentTopIdx = this.findCurrentTopIdx()
        if (currentTopIdx !== -1 && currentTopIdx !== idx && newTopEmail) {
          this.$set(this.internalPeople, currentTopIdx, {
            ...this.internalPeople[currentTopIdx],
            managerEmail: newTopEmail,
          })
        }
        // Promote this row + put them on the company-name team if we
        // have one. The company name represents the top-most team in
        // the hierarchy; the user's pre-existing team choice is
        // overridden because their role just changed.
        const promoted = { ...row, managerEmail: null }
        if (this.companyName) {
          promoted.team = this.companyName
        }
        this.$set(this.internalPeople, idx, promoted)
        this.emit()
        return
      }
      this.updateField(idx, 'managerEmail', email)
    },
    onDocumentClick(e) {
      if (this.openManagerIdx === -1) return
      const target = e.target
      if (target && target.closest && target.closest('.manager-cell-wrap')) return
      this.openManagerIdx = -1
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../_shared.scss';

.team-builder {
  display: block;
}

/* =====================================================================
   People table — bordered card with a header strip and one row per
   person. The signer row is highlighted via `.people-row.you` (purple
   tinted gradient).
   ===================================================================== */
.people-table {
  border: 1px solid var(--signup-border-soft);
  border-radius: 12px;
  overflow: visible; // allow the manager dropdown to spill out
  background: var(--signup-surface);
  box-shadow: 0 1px 2px rgba(30, 10, 60, 0.04);
}

/* =====================================================================
   Dense mode — kicks in at 10+ people. Reduces row height ~10px and
   trims internal paddings/font-sizes so a 20-row free-tier max table
   stays roughly viewport-fitable on a 900px-tall screen. The header
   row stays the same. Manager dropdown items aren't compressed —
   they live in a popover with room to breathe.
   ===================================================================== */
.people-table--dense {
  .people-row {
    padding: 2px 4px;
  }
  .people-row > .cell {
    padding: 2px 6px;
  }
  .name-cell {
    padding: 2px 0 2px 2px;
    gap: 7px;
  }
  .name-cell .input,
  .input.cell-plain,
  .manager-cell {
    height: 28px;
    font-size: 12.5px;
  }
  .name-cell .input,
  .input.cell-plain {
    padding: 4px 8px;
  }
  .manager-cell {
    padding: 4px 7px;
    gap: 6px;
  }
  .person-dot {
    width: 8px;
    height: 8px;
  }
  .you-badge {
    font-size: 8px;
    padding: 1px 5px;
    letter-spacing: 0.06em;
  }
  .people-row__remove {
    width: 24px;
    height: 24px;
    font-size: 14px;
  }
  .static {
    font-size: 12.5px;
  }
}

.people-table__head,
.people-row {
  display: grid;
  /* Name and Manager get more width than Email/Team — names tend to be
     longer than emails and need the room since they include the dot
     marker + the editable input. */
  grid-template-columns: 1.7fr 1.5fr 1fr 1.5fr 28px;
  align-items: center;
}

.people-table__head {
  background: var(--signup-surface-subtle);
  border-bottom: 1px solid var(--signup-border-soft);
  padding: 0 4px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.people-table__head > div {
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 11px;
  font-weight: 800;
  color: var(--signup-text-muted);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 10px 12px;
}

.people-row {
  border-bottom: 1px solid var(--signup-border-soft);
  padding: 6px 4px;
  transition: background 120ms ease;
  position: relative;
}

.people-row:last-child { border-bottom: none; }
.people-row:hover:not(.you) { background: rgba(108, 48, 204, 0.025); }
.people-row.you {
  background: linear-gradient(90deg, rgba(108, 48, 204, 0.05) 0%, rgba(108, 48, 204, 0.01) 100%);
  // Subtle left edge accent — reinforces the "you sit on top" hierarchy.
  box-shadow: inset 3px 0 0 0 var(--primary-faint);
}

.people-row > .cell {
  padding: 4px 6px;
  min-width: 0;
}

/* =====================================================================
   Avatar circle — shared by name + manager cells. The five `.cN`
   buckets mirror the mockup palette so colors stay consistent across
   the table when an email reappears in a manager dropdown.
   ===================================================================== */
.avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.avatar.sm { width: 20px; height: 20px; font-size: 9px; }
.avatar.you { background: var(--signup-brand-deep); }
.avatar.c0  { background: #c4c4d0; color: #4a4a5a; }
.avatar.c1  { background: #6C30CC; }
.avatar.c2  { background: #5DADE2; }
.avatar.c3  { background: #3AC47D; }
.avatar.c4  { background: #FF9671; }
.avatar.c5  { background: #FFC200; color: #1a1a2e; }

/* =====================================================================
   Name cell — avatar + name input/static label.
   ===================================================================== */
.name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0 4px 4px;
  min-width: 0;
}

.name-cell .input {
  flex: 1;
  min-width: 0;
  height: 32px;
  font-size: 13.5px;
  padding: 6px 10px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 8px;
  font-family: inherit;
  color: var(--signup-text);
  transition: background 120ms ease, border-color 120ms ease;
}

.name-cell .input:hover:not(:focus) {
  background: var(--signup-surface);
  border-color: var(--signup-border-soft);
}

.name-cell .input:focus {
  outline: none;
  background: var(--signup-surface);
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-faint);
}

.you-name {
  flex: 1;
  font-weight: 700;
  font-size: 13.5px;
  color: var(--signup-text);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.you-badge {
  background: var(--signup-brand-faint);
  color: var(--signup-brand);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 999px;
  flex-shrink: 0;
}

/* =====================================================================
   Person dot — compact identifier replacing the bigger initials avatar
   in row contexts (name + manager cells). Same color buckets as
   `.avatar` so the visual rhythm is preserved, just smaller. The
   manager dropdown still uses the larger initials avatar (more space
   inside the dropdown row).
   ===================================================================== */
.person-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;

  &.you  { background: var(--signup-brand-deep); }
  &.c0   { background: #c4c4d0; box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06); }
  &.c1   { background: #6C30CC; }
  &.c2   { background: #5DADE2; }
  &.c3   { background: #3AC47D; }
  &.c4   { background: #FF9671; }
  &.c5   { background: #FFC200; }
}

/* =====================================================================
   Generic cell-plain input/select — borderless until hover/focus, then
   drops in a soft surface + border. Used by email, team, and the
   inline new-team draft.
   ===================================================================== */
.input.cell-plain,
select.input.cell-plain {
  width: 100%;
  height: 32px;
  font-size: 13px;
  padding: 6px 10px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 8px;
  font-family: inherit;
  color: var(--signup-text);
  transition: background 120ms ease, border-color 120ms ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.input.cell-plain:hover:not(:focus) {
  background: var(--signup-surface);
  border-color: var(--signup-border-soft);
}

.input.cell-plain:focus {
  outline: none;
  background: var(--signup-surface);
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-faint);
}

.input.cell-plain.has-error {
  border-color: var(--signup-danger);
  background: var(--signup-surface);
}

.cell-error {
  display: block;
  margin-top: 2px;
  padding-left: 10px;
  font-size: 11px;
  font-weight: 600;
  color: var(--signup-danger);
}

.static {
  padding-left: 10px;
  font-size: 13px;
  color: var(--signup-text-muted);
}

.static.email { color: var(--signup-text-soft); }

/* =====================================================================
   Manager cell — faux dropdown trigger that opens an absolute-positioned
   list of candidates above this row. Stops at z-index 5 to clear the
   sibling rows but stay below any modal chrome.
   ===================================================================== */
.manager-cell-wrap {
  position: relative;
}

.manager-cell {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  height: 32px;
  transition: background 120ms ease, border-color 120ms ease;
  background: transparent;
  font-family: inherit;
  min-width: 0;
}

.manager-cell:hover {
  background: var(--signup-surface);
  border-color: var(--signup-border-soft);
}

.manager-cell__name {
  flex: 1;
  font-size: 13px;
  color: var(--signup-text);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.manager-cell__name--placeholder {
  color: var(--signup-text-muted);
  font-weight: 500;
}

.manager-cell--top .manager-cell__name {
  color: var(--signup-brand);
  font-weight: 700;
}

.manager-cell__top-marker {
  width: 20px;
  height: 20px;
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: var(--signup-gold-faint);
  color: var(--signup-gold);
  font-size: 11px;
  flex-shrink: 0;
}

.manager-cell__caret {
  color: var(--signup-text-muted);
  flex-shrink: 0;
  font-size: 10px;
}

.manager-dropdown {
  list-style: none;
  margin: 4px 0 0;
  padding: 4px;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 5;
  background: var(--signup-surface);
  border: 1px solid var(--signup-border-soft);
  border-radius: 10px;
  box-shadow: var(--signup-shadow-card);
  max-height: 220px;
  overflow-y: auto;
}

.manager-dropdown__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: var(--signup-text);
  transition: background 120ms ease;

  &:hover { background: var(--primary-faint); }
}

.manager-dropdown__item--clear {
  color: var(--signup-text-muted);
  font-style: italic;
  font-size: 12.5px;
}

.manager-dropdown__item--top {
  background: var(--signup-gold-faint);
  border-bottom: 1px solid var(--signup-border-soft);
  margin-bottom: 4px;
  padding: 8px;
  border-radius: 6px;

  &:hover {
    background: rgba(247, 185, 36, 0.18);
  }

  .manager-cell__top-marker {
    background: rgba(247, 185, 36, 0.25);
  }
}

.manager-dropdown__hint {
  font-size: 11px;
  color: var(--signup-text-muted);
  font-weight: 500;
  margin-left: auto;
}

.manager-dropdown__name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.manager-dropdown__empty {
  padding: 8px 10px;
  font-size: 12px;
  color: var(--signup-text-muted);
}

/* =====================================================================
   Remove button — always visible on editable rows for discoverability,
   muted by default, danger-tinted on hover.
   ===================================================================== */
.people-row__remove {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: var(--signup-text-muted);
  cursor: pointer;
  font-size: 16px;
  margin: 0 auto;
  display: grid;
  place-items: center;
  transition: background 120ms ease, color 120ms ease;
}

.people-row__remove:hover {
  background: rgba(204, 48, 48, 0.08);
  color: var(--signup-danger);
}

/* =====================================================================
   Add another person — full-width dashed brand button. Mirrors the
   "+ Add another team" affordance from the legacy UI but with stronger
   purple emphasis to signal it as the primary row-adding action.
   ===================================================================== */
.add-person {
  appearance: none;
  margin-top: 10px;
  background: transparent;
  border: 1px dashed rgba(108, 48, 204, 0.35);
  border-radius: 12px;
  padding: 11px 14px;
  color: var(--primary);
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  transition: background 120ms ease, border-color 120ms ease;

  &:hover {
    background: rgba(108, 48, 204, 0.04);
    border-color: var(--primary);
  }

  &:focus-visible {
    outline: 2px solid var(--signup-gold);
    outline-offset: 2px;
  }
}

/* =====================================================================
   Live counter row — total people + team count on the left, helper hint
   on the right. Counter flips red when over the cap.
   ===================================================================== */
.people-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 14px;
  font-family: 'Lato', system-ui, sans-serif;
  font-size: 12px;
  color: var(--signup-text-muted);
}

.people-meta strong {
  color: var(--signup-text);
  font-weight: 800;
}

.people-meta__sep {
  margin: 0 4px;
  color: var(--signup-border-soft);
}

.people-meta__hint {
  color: var(--signup-text-muted);
}

.people-meta--over,
.people-meta--over strong {
  color: var(--signup-danger);
}

/* Per-cell label — hidden on desktop (the table head provides labels)
   and shown on mobile where each row stacks into its own card. */
.cell-label { display: none; }

@media (max-width: 720px) {
  .people-table__head,
  .people-row {
    grid-template-columns: 1.4fr 1.4fr 1fr 1.2fr 28px;
  }
}

/* =====================================================================
   Mobile (<= 540px): the table dissolves into a stack of per-person
   cards. The header strip disappears (replaced by per-cell labels), each
   row gets a clear bordered card with a top-right remove button, and
   inputs always show borders so editable fields read as editable rather
   than blending into static text. Touch targets bump to 40px so taps
   don't fall between fields.
   ===================================================================== */
@media (max-width: 540px) {
  .people-table {
    border: none;
    background: transparent;
    box-shadow: none;
    overflow: visible;
  }
  .people-table__head { display: none; }

  .people-row {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 14px 14px 16px;
    margin-bottom: 12px;
    border: 1px solid var(--signup-border-soft);
    border-radius: 12px;
    background: var(--signup-surface);
    box-shadow: 0 1px 2px rgba(30, 10, 60, 0.04);
    /* .people-row already has position: relative — needed to anchor
       the absolute remove button. */
  }
  .people-row:last-child { border-bottom: 1px solid var(--signup-border-soft); }
  .people-row:hover:not(.you) { background: var(--signup-surface); }
  .people-row.you {
    border-color: rgba(108, 48, 204, 0.25);
    box-shadow: 0 1px 2px rgba(108, 48, 204, 0.08), inset 3px 0 0 0 var(--primary-faint);
  }

  .people-row > .cell { padding: 0; }
  /* Reserve space for the absolutely-positioned remove button so it
     doesn't overlap the Name input's top-right corner. Skipped for
     the signer card where the button is hidden. */
  .people-row:not(.you) > .cell:first-child { padding-right: 44px; }

  .cell-label {
    display: block;
    font-family: 'Lato', system-ui, sans-serif;
    font-size: 10px;
    font-weight: 800;
    color: var(--signup-text-muted);
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  .name-cell {
    padding: 0;
    gap: 8px;
  }
  .name-cell .input,
  .input.cell-plain,
  select.input.cell-plain,
  .manager-cell {
    height: 40px;
    font-size: 14px;
    background: var(--signup-surface);
    border: 1px solid var(--signup-border-soft);
    border-radius: 10px;
  }
  .name-cell .input,
  .input.cell-plain {
    padding: 8px 12px;
  }
  .manager-cell {
    padding: 6px 10px 6px 12px;
  }
  /* Static (signer) email reads as a value, not a placeholder — give
     it the same visual frame as the editable inputs so the layout
     doesn't jump between rows. */
  .static.email {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 12px;
    background: var(--signup-surface-subtle);
    border: 1px solid var(--signup-border-soft);
    border-radius: 10px;
    color: var(--signup-text-muted);
    font-size: 14px;
  }

  /* Float remove × into the card's top-right corner. Its containing
     .cell is taken out of the grid flow via position: absolute, so the
     stacked card collapses cleanly to four labeled fields. */
  .people-row > .cell:last-child {
    position: absolute;
    top: 8px;
    right: 8px;
    width: auto;
    padding: 0;
  }
  .people-row__remove {
    width: 36px;
    height: 36px;
    margin: 0;
    font-size: 18px;
  }
  /* Signer card has no remove button — make sure the empty cell takes
     no space (display: none keeps it out of the accessibility tree). */
  .people-row.you > .cell:last-child { display: none; }

  /* Manager dropdown — full-width within the card, slightly taller hit
     targets for finger taps. */
  .manager-dropdown {
    max-height: 260px;
  }
  .manager-dropdown__item {
    padding: 10px 10px;
    font-size: 14px;
  }

  /* The "you" badge and avatars don't need to shrink — keep them
     readable. The row's left-edge accent moves into the card border
     via the .you box-shadow above. */

  /* Footer counter — stack on very narrow screens to avoid the meta
     line wrapping awkwardly between count and hint. */
  .people-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}
</style>
